import React, { FC, useEffect, useRef, useState, useCallback, Fragment } from 'react';
import styles from './PageBuilder.module.css';
import HeaderSection from '../HeaderSection/HeaderSection';
import {
  Page,
  removePageById,
  saveSelectedItem,
  selectTemplateDocument,
  toggleIndividualVisibilityFooter,
  toggleIndividualVisibilityHeader,
  togglePageOrientation,
  togglePageOrientationById,
  selectPageOrientationById,
  updatePageById,
} from 'features/document/documentSlice';
import ContentSection from '../ContentSection/ContentSection';
import PageBreak from './components/PageBreak/PageBreak';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useDrop } from 'react-dnd';
import { PagePartType, ElementSide } from 'features/document/constants';
import { addRowContentBlock } from 'features/document/documentSlice';
import { XYCoord } from 'dnd-core';
import { useDropContext, useDropUpdateContext } from '../DropContext';
import FooterSection from '../FooterSection/FooterSection';
import { PageToolbar } from './components/PageToolbar/PageToolbar';
import { useGetFileQuery } from 'features/files/filesApi';
import { PageContainer } from './PageBuilder.styled';
import ModalFactory from 'components/CustomModal/ModalFactory';
import { Box } from '@mui/material';

interface Props {
  content: Page;
  zoom: number;
  pageNumber: number;
  totalPages: number;
}
interface DragElement {
  pageId: string;
  index: number;
  rowId: string;
  columnId: string;
  currentElement: Element;
}

type DraggableTypes =
  | { type: PagePartType.Block; elementType: string }
  | ({ type: PagePartType.Element } & DragElement);

const PageBuilder: FC<Props> = ({ content, zoom, totalPages, pageNumber }) => {
  const {
    header,
    id,
    isIndividualHeaderVisible,
    rows,
    isIndividualFooterVisible,
    footer,
    hideHeaderAndFooterOnOverflow,
  } = content;
  const dispatch = useAppDispatch();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pageRef = useRef<any>(null);
  const [isDeletePageModalOpen, setIsDeletePageModalOpen] = useState(false);
  const [modalData, setModalData] = useState({});
  const { isOrientedHorizontally } = useAppSelector(selectTemplateDocument);
  const isOrientedHorizontallySinglePage = useAppSelector(state => selectPageOrientationById(state, id));

  const { data: backgroundImageData } = useGetFileQuery(Number(content.backgroundImageId), {
    skip: !content.backgroundImageId,
  });

  const headerHeight = 63;
  const showRemovePageButton = totalPages > 1;

  const [borderHighlight, setBorderHighlight] = useState({
    top: false,
    bottom: false,
  });

  const dropStatus = useDropContext();
  const updateStatus = useDropUpdateContext();

  const updateBorderHighlight = useCallback(
    (clientOffset: XYCoord, isOver: boolean) => {
      const hoverBoundingRect = pageRef.current?.getBoundingClientRect();
      const zoomedHeaderHeight = (headerHeight * zoom) / 100;
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;
      setBorderHighlight({
        top: isOver && hoverClientY < zoomedHeaderHeight,
        bottom: isOver && hoverClientY > zoomedHeaderHeight,
      });
    },
    [zoom]
  );
  const [breaksNumber, setBreaksNumber] = useState<number>(0);
  const pageHeight = 1054;

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { height } = entry.contentRect;
        const breaks = height > pageHeight ? Math.trunc(height / pageHeight) : 0;
        setBreaksNumber(breaks);
      }
    });

    if (pageRef.current) {
      resizeObserver.observe(pageRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    dispatch(
      updatePageById({
        id: id,
        property: 'pageBreaks',
        value: breaksNumber,
      })
    );
  }, [breaksNumber]);

  useEffect(() => {
    if (hideHeaderAndFooterOnOverflow) {
      dispatch(
        toggleIndividualVisibilityHeader({
          pageId: id,
          isVisible: hideHeaderAndFooterOnOverflow,
        })
      );

      dispatch(
        toggleIndividualVisibilityFooter({
          pageId: id,
          isVisible: hideHeaderAndFooterOnOverflow,
        })
      );
    }
  }, [hideHeaderAndFooterOnOverflow]);

  const pageBreaks = Array.from({ length: breaksNumber }, (_, index) => index);

  const [{ isOver }, drop] = useDrop<DraggableTypes, void, { isOver: boolean }>(
    () => ({
      accept: [PagePartType.Element, PagePartType.Block],
      collect: monitor => ({
        isOver: monitor.isOver(),
      }),

      hover: (item, monitor) => {
        if (!pageRef.current) {
          return;
        }

        updateStatus({ page: true, row: false, element: false });

        const clientOffset = monitor.getClientOffset();

        if (clientOffset) {
          updateBorderHighlight(clientOffset as XYCoord, isOver);
        }
      },

      drop: (item, _monitor) => {
        if (!dropStatus.row && !dropStatus.element) {
          const { type } = item;
          const isBlock = type === PagePartType.Block;
          if (isBlock) {
            if (borderHighlight.top || !rows.length) {
              dispatch(
                addRowContentBlock({
                  pageId: id,
                  rowIndex: 0,
                  side: ElementSide.Top,
                  type: item.elementType,
                })
              );
            } else if (borderHighlight.bottom) {
              dispatch(
                addRowContentBlock({
                  pageId: id,
                  rowIndex: rows.length,
                  side: ElementSide.Top,
                  type: item.elementType,
                })
              );
            }
          }
        }
      },
    }),
    [borderHighlight]
  );

  drop(pageRef);

  const handleSelectedItem = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();
    dispatch(saveSelectedItem({ data: { data: content, pageId: id, type: PagePartType.Page } }));
  };

  const handleOnClickOrientation = () => {
    dispatch(togglePageOrientation(!isOrientedHorizontally));
  };

  const openModal = (data: { pageId: string }) => {
    setModalData({ ...data });
    setIsDeletePageModalOpen(true);
  };

  const closeModal = () => {
    setIsDeletePageModalOpen(false);
  };

  const modalProps = {
    onClose: closeModal,
    modalData: modalData,
  };

  const handleOnClickRemove = () => {
    if (content.rows.length > 0) {
      openModal({ pageId: id });
    } else {
      dispatch(removePageById(id));
    }
  };

  const handleOnClickPageOrientation = () => {
    dispatch(togglePageOrientationById({ pageId: id }));
  };

  const getPageOrientation = (isOrientedHorizontallySinglePage: boolean, isOrientedHorizontally: boolean) => {
    if (isOrientedHorizontally) {
      return styles.horizontalPage;
    }
    return isOrientedHorizontallySinglePage ? styles.horizontalPage : styles.verticalPage;
  };

  return (
    <Fragment>
      {isDeletePageModalOpen && <ModalFactory type='delete-page' props={modalProps} />}
      <PageToolbar
        id={id}
        isOrientedHorizontally={isOrientedHorizontally}
        isOrientedHorizontallySinglePage={isOrientedHorizontallySinglePage}
        onClickSettings={handleSelectedItem}
        onClickOrientation={handleOnClickOrientation}
        onClickRemove={handleOnClickRemove}
        onClickPageOrientation={handleOnClickPageOrientation}
        showRemovePageButton={showRemovePageButton}
      />
      <PageContainer
        id={id}
        className={`${styles.page} ${getPageOrientation(isOrientedHorizontallySinglePage, isOrientedHorizontally)}`}
        ref={pageRef}
        backgroundImage={content.showBackgroundImage && content.backgroundImageId ? backgroundImageData?.data.url : ''}
        backgroundPosition={content.alignment || 'center'}
        backgroundImageOpacity={content.backgroundImageOpacity || 1}
        backgroundColor={content.showBackgroundColor ? content.backgroundColor || 'white' : 'white'}
      >
        {!hideHeaderAndFooterOnOverflow && (
          <HeaderSection pageId={id} header={header} isIndividualHeaderVisible={isIndividualHeaderVisible} />
        )}
        <ContentSection pageId={id} rows={rows} />
        {pageBreaks.map(index => (
          <PageBreak key={index} pageHeight={pageHeight} pageBreakNumber={index + 1} />
        ))}
        <Box
          className={styles.footerPosition}
          sx={{ visibility: hideHeaderAndFooterOnOverflow ? 'hidden' : 'visible' }}
        >
          <FooterSection
            pageId={id}
            footer={footer}
            isIndividualFooterVisible={isIndividualFooterVisible}
            totalPages={totalPages}
            pageNumber={pageNumber}
          />
        </Box>
      </PageContainer>
    </Fragment>
  );
};

export default PageBuilder;
